import * as React from "react";
import { BookOpen, Bell, Zap, Bug, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  changelogEntries,
  ChangelogEntry,
  getCurrentBookRevision,
} from "./changelog-data";

interface TranslatedEntry {
  id: string;
  title: string;
  description: string;
}

interface ChangelogTimelineProps {
  className?: string;
  /** Signed-in user, if any. Only used to soften the sign-in hint. */
  user?: { email: string | null } | null;
}

const TYPE_ICON: Record<ChangelogEntry["type"], React.ComponentType<{ className?: string }>> = {
  book_update: BookOpen,
  general_news: Bell,
  feature: Zap,
  bug_fix: Bug,
};

/**
 * Full-page changelog. Unlike the compact `Changelog` card used on the home
 * page and dashboard, this shows every entry at once: no collapsing, no inner
 * scroll area, no "show more". Someone who opened /changelog came to read it.
 */
const ChangelogTimeline: React.FC<ChangelogTimelineProps> = ({
  className = "",
  user,
}) => {
  const { t, i18n } = useTranslation("home");

  const bundle = i18n.getResourceBundle(i18n.language, "changelog") as
    | { entries?: TranslatedEntry[] }
    | undefined;
  const translated = bundle?.entries || [];

  const entries: ChangelogEntry[] = changelogEntries.map((entry) => {
    const match = translated.find((te) => te.id === entry.id);
    return {
      ...entry,
      title: match?.title || entry.title,
      description: match?.description || entry.description,
    };
  });

  const currentRevision = getCurrentBookRevision();

  const localeMap: Record<string, string> = {
    en: "en-US",
    fr: "fr-FR",
    de: "de-DE",
    es: "es-ES",
    zh: "zh-CN",
    ja: "ja-JP",
  };
  const locale = localeMap[i18n.language] || "en-US";

  const formatDate = (value: string) =>
    new Date(value).toLocaleDateString(locale, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  if (!entries.length) return null;

  return (
    <div className={className}>
      {currentRevision && (
        <div className="border border-secondary rounded-lg p-5 mb-10 bg-secondary/20">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2">
            <span className="text-sm text-secondary">
              {t("changelogPage.currentRevisionLabel")}
            </span>
            <span className="font-mono text-lg text-primary">
              {currentRevision}
            </span>
          </div>
          <p className="text-sm text-secondary leading-relaxed">
            {t("changelogPage.compareHint")}
          </p>
        </div>
      )}

      <ol className="relative">
        {/* Continuous rail behind the markers. */}
        <span
          aria-hidden="true"
          className="absolute left-[7px] top-2 bottom-2 w-px bg-secondary"
        />

        {entries.map((entry) => {
          const Icon = TYPE_ICON[entry.type] || Bell;
          return (
            <li key={entry.id} className="relative pl-9 pb-10 last:pb-0">
              <span
                aria-hidden="true"
                className="absolute left-0 top-1.5 w-[15px] h-[15px] rounded-full border-2 border-secondary bg-primary"
              />

              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-1.5">
                <time
                  dateTime={entry.date}
                  className="text-xs uppercase tracking-wide text-secondary"
                >
                  {formatDate(entry.date)}
                </time>
                {entry.bookRevision && (
                  <span className="font-mono text-xs text-primary border border-secondary rounded px-1.5 py-0.5">
                    {entry.bookRevision}
                  </span>
                )}
              </div>

              <h3 className="text-base font-medium text-primary leading-snug mb-1.5 flex items-start gap-2">
                <Icon className="w-4 h-4 mt-1 flex-shrink-0 text-secondary" />
                <span>{entry.title}</span>
              </h3>

              <p className="text-[15px] text-secondary leading-relaxed max-w-2xl">
                {entry.description}
              </p>

              {entry.ctaUrl && (
                <a
                  href={entry.ctaUrl}
                  className="inline-block mt-2 text-sm text-accent hover:text-accent/80 font-medium transition-colors"
                >
                  {t(entry.ctaLabelKey ?? "announcement.open")} →
                </a>
              )}

              {entry.requiresSignIn && !user && (
                <p className="mt-2 text-sm text-secondary flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5 flex-shrink-0" />
                  {t("changelogPage.signInNote")}
                </p>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default ChangelogTimeline;
export { ChangelogTimeline };
